import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import LocalizedText from '@LocalizedText';
import LocalizedImage from '@LocalizedImage';
import {
  HELP_SECTION_WHITE_INK,
  HELP_TOPIC_WHITE_INK,
  HELP_TOPIC_WHITE_INK_OPTIONS,
  HELP_TOPIC_WHITE_INK_IN_YOUR_UPLOAD,
  HELP_WHITE_INK_LABELS,
} from '@app/constants';
import { setLabelTypePart } from '@app/utils/help';

const WhiteInkSection = ({ activeTopic, scrollToTopic, isSheetFlow }) => {
  const labelTypePart = setLabelTypePart(HELP_WHITE_INK_LABELS, [isSheetFlow]);

  useEffect(() => {
    activeTopic && scrollToTopic(activeTopic);
  }, [activeTopic, scrollToTopic]);

  return (
    <div id={HELP_SECTION_WHITE_INK} className="help-section">
      <div id={HELP_TOPIC_WHITE_INK} className="section-topic">
        <LocalizedText id="qau-help-topic-white-ink" tagName="h2" />
        <div>
          <LocalizedText id={`qau-help-topic-white-ink-${labelTypePart}p1`} tagName="p" />
          <LocalizedText id={`qau-help-topic-white-ink-${labelTypePart}p2`} tagName="p" />
        </div>
      </div>
      <div className="section-topic">
        <LocalizedText id="qau-help-topic-white-ink-clear-material" tagName="h3" />
        <div>
          <LocalizedText id={`qau-help-topic-white-ink-clear-material-${labelTypePart}p1`} tagName="p" />
          <LocalizedText id={`qau-help-topic-white-ink-clear-material-${labelTypePart}p2`} tagName="p" />
          <LocalizedText id={`qau-help-topic-white-ink-clear-material-${labelTypePart}p3`} tagName="p" />
          <div className="image-block white-underprint-clear">
            <LocalizedImage name="white-underprint-clear.jpg" alt="White underprint clear" bordered={true} width={600} />
          </div>
          <LocalizedText id={`qau-help-topic-white-ink-clear-material-${labelTypePart}p4`} tagName="p" />
        </div>
      </div>
      <div className="section-topic">
        <LocalizedText id={`qau-help-topic-white-ink-chrome-${labelTypePart}metalic`} tagName="h3" />
        <div>
          <LocalizedText id={`qau-help-topic-white-ink-chrome-metalic-${labelTypePart}p1`} tagName="p" />
          <div className="image-block white-underprint-chrome">
            <LocalizedImage name="white-underprint-chrome.jpg" alt="White underprint chrome" bordered={true} width={600} />
          </div>
          <LocalizedText id={`qau-help-topic-white-ink-chrome-metalic-${labelTypePart}p2`} tagName="p" />
        </div>
      </div>
      <div id={HELP_TOPIC_WHITE_INK_OPTIONS} className="section-topic">
        <LocalizedText id="qau-help-topic-white-ink-options" tagName="h3" />
        <LocalizedText id={`qau-help-topic-white-ink-options-${labelTypePart}p1`} tagName="p" />
        <div className="image-block white-ink-options">
          <LocalizedImage name={`white-ink-options.png`} alt="White Ink Options" bordered={true} width={600} />
        </div>
        <LocalizedText id={`qau-help-topic-white-ink-options-${labelTypePart}p2`} tagName="p" />
        <LocalizedText id={`qau-help-topic-white-ink-options-${labelTypePart}p3`} tagName="p" />
        <div>
          <LocalizedText id="qau-help-topic-white-ink-keep-all-white" />
          <ul>
            <LocalizedText id="qau-help-topic-white-ink-keep-all-white-l1" tagName="li" />
          </ul>
          <LocalizedText id="qau-help-topic-white-ink-remove-all-white" />
          <ul>
            <LocalizedText id={`qau-help-topic-white-ink-remove-all-white-${labelTypePart}l1`} tagName="li" />
          </ul>
          <LocalizedText id="qau-help-topic-white-ink-remove-white-background" />
          <ul>
            <LocalizedText id={`qau-help-topic-white-ink-remove-white-background-${labelTypePart}l1`} tagName="li" />
          </ul>
          <LocalizedText id={`qau-help-topic-white-ink-select-white-remove`} />
          <ul>
            <LocalizedText id={`qau-help-topic-white-ink-select-white-remove-l1`} tagName="li" />
          </ul>
        </div>
        <LocalizedText id={`qau-help-topic-white-ink-${labelTypePart}p4`} tagName="p" />
        <LocalizedText id={`qau-help-topic-white-ink-${labelTypePart}p5`} tagName="p" />
      </div>
      <div className="section-topic">
        <LocalizedText id="qau-help-topic-white-ink-advanced-mode" tagName="h3" />
        {isSheetFlow && <LocalizedText id="qau-help-topic-white-ink-advanced-mode-sheet-p" tagName="p" />}
        <div className="picture-section image-block">
          <LocalizedImage name="white-ink-advanced-p1.png" alt="White Ink Advanced" bordered={true} width={250} />
          <LocalizedImage name="white-ink-advanced-p2.png" alt="White Ink Advanced" bordered={true} width={250} />
        </div>
        <div>
          <LocalizedText id={`qau-help-topic-white-ink-advanced-mode-${labelTypePart}p1`} tagName="p" />
          <LocalizedText id={`qau-help-topic-white-ink-advanced-mode-${labelTypePart}p2`} tagName="p" />
          <ul>
            <LocalizedText id={`qau-help-topic-white-ink-advanced-mode-${labelTypePart}l1`} tagName="li" />
            <LocalizedText id={`qau-help-topic-white-ink-advanced-mode-${labelTypePart}l2`} tagName="li" />
            <LocalizedText id={`qau-help-topic-white-ink-advanced-mode-${labelTypePart}l3`} tagName="li" />
            <LocalizedText id={`qau-help-topic-white-ink-advanced-mode-${labelTypePart}l4`} tagName="li" />
          </ul>
          <LocalizedText id={`qau-help-topic-white-ink-advanced-mode-${labelTypePart}p3`} tagName="p" />
        </div>
      </div>
      {isSheetFlow && (
        <div id={HELP_TOPIC_WHITE_INK_IN_YOUR_UPLOAD} className="section-topic">
          <LocalizedText id="qau-help-topic-white-ink-in-your-upload" tagName="h3" />
          <LocalizedText id="qau-help-topic-white-ink-in-your-upload-p1" tagName="p" />
          <LocalizedText id="qau-help-topic-white-ink-in-your-upload-p2" tagName="p" />
          <LocalizedText id="qau-help-topic-white-ink-in-your-upload-p3" tagName="p" />
        </div>
      )}
    </div>
  );
};

WhiteInkSection.propTypes = {
  scrollToTopic: PropTypes.func.isRequired,
  activeTopic: PropTypes.string,
  isSheetFlow: PropTypes.bool.isRequired,
};

export default WhiteInkSection;
